import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Bar, BarChart, Tooltip, YAxis, ResponsiveContainer, CartesianGrid, XAxis } from 'recharts';
import mixpanel from 'mixpanel-browser';

import {
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  IconButton,
  LinearProgress,
} from '@material-ui/core';
import { Alert, Tooltip as MuiTooltip, Divider } from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { convertCurrency } from 'src/utils/functions';
import {
  CalculatorInputField,
  profitCalReturnObj,
  Revenue,
  ProfitCalculatorValues,
} from 'src/interfaces/idashboard';
import { infoIconStyle } from '../../Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  DARK_GREY,
  exchangeRates,
  rentalArbitrageCalculatorInputFields,
} from 'src/const';
import TooltipCard from './TooltipCard';
import InfoTooltip from '../Tooltips/InfoTooltip';
import { RevenueSimulationCardTypes } from 'src/interfaces/iproptypes';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  profitGraphRoot: {
    flex: 1,
    maxHeight: 375,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      maxHeight: 370,
    },
  },
  iconButton: {
    padding: 5,
  },
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
}));

const NumberFormatCustom = raw => {
  return <NumberFormat value={raw} displayType={'text'} thousandSeparator />;
};

const months = [3, 6, 9, 12, 24];
const calculateProfit = (
  monthlyRev: number,
  monthlyExpense: number,
  monthlyRent: number,
  oneTimeCost: number
) => {
  const monthlyCost = monthlyExpense + monthlyRent;
  const monthlyProfit = Math.round(monthlyRev - monthlyCost);
  const graphVal = months.map(nMonths => {
    const nMonthsTotalExpense = Math.round(monthlyCost * nMonths + oneTimeCost);
    const nMonthsTotalRev = Math.round(monthlyRev * nMonths);
    return {
      nMonths,
      nMonthsTotalExpense,
      nMonthsTotalRev,
    };
  });

  return {
    monthlyProfit: monthlyProfit,
    graphVal,
  };
};

const RentalArbitrageSimulationCard = ({
  dynamicRev,
  isDisabled,
  dynamicTrigger,
  currency,
}: RevenueSimulationCardTypes) => {
  const classes = useStyles();

  const avgRev = dynamicRev;
  // dynamicRev.reduce(
  //   (partial_sum: number, revObj: Revenue) => partial_sum + (revObj.revenue as number),
  //   0
  // ) / dynamicRev.length;

  const [profitCalVals, setProfitCalVals] = useState<ProfitCalculatorValues>({
    revenue: 0,
    monthlyRent: 0,
    monthlyExpense: 0,
    oneTimeCost: 0,
  });

  const handleProfitCalValueChange = (prop: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfitCalVals({ ...profitCalVals, [prop]: event.target.value });
  };

  const [allProfitCalInputsFilled, setAllProfitCalInputsFilled] = useState<boolean>(false);
  useEffect(() => {
    if (
      profitCalVals.monthlyRent &&
      profitCalVals.monthlyExpense &&
      profitCalVals.oneTimeCost &&
      !allProfitCalInputsFilled
    ) {
      mixpanel.track('allCalculatorInputsFilled');
      setAllProfitCalInputsFilled(true);
    }
  }, [profitCalVals]);

  useEffect(() => {
    if (setProfitCalVals) {
      setProfitCalVals({
        ...(profitCalVals as ProfitCalculatorValues),
        revenue: convertCurrency(currency, avgRev),
      });
    }
  }, [avgRev, currency]);

  const inputFields: CalculatorInputField[] = rentalArbitrageCalculatorInputFields;

  const profitCalReturnObj: profitCalReturnObj = calculateProfit(
    convertCurrency(currency, avgRev),
    Number((profitCalVals as ProfitCalculatorValues).monthlyExpense),
    Number((profitCalVals as ProfitCalculatorValues).monthlyRent),
    Number((profitCalVals as ProfitCalculatorValues).oneTimeCost)
  );

  const hoverGraph = () => {
    mixpanel.track('checkTooltipOnInvestmentDashboard');
  };

  const CustomTooltip = ({ active, payload, label, classes }) => {
    if (active && payload) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {payload[0].payload.nMonths} months Total Expense: {exchangeRates[currency].symbol}
              {payload[0].payload.nMonthsTotalExpense}
              <br />
              {payload[0].payload.nMonths} months Total Revenue: {exchangeRates[currency].symbol}
              {payload[0].payload.nMonthsTotalRev}
              <Divider style={{ background: DARK_GREY, marginTop: 10, marginBottom: 10 }} />
              {payload[0].payload.nMonths} months Total Profit: {exchangeRates[currency].symbol}
              {payload[0].payload.nMonthsTotalRev - payload[0].payload.nMonthsTotalExpense}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <>
      {/* <Card>
      <CardContent> */}
      <Grid container spacing={2} id="calculator">
        {dynamicTrigger && (
          <Grid item className={classes.linearProgress}>
            <LinearProgress />
          </Grid>
        )}

        <Grid item style={{ width: '100%' }}>
          <Typography>
            {' '}
            Revenue & Expense Simulation
            <MuiTooltip title="This simulation uses the past 12 months' average revenue from the listings that you are seeing in the map view.">
              <IconButton className={classes.iconButton}>
                <InfoOutlinedIcon style={infoIconStyle} />{' '}
              </IconButton>
            </MuiTooltip>
          </Typography>
        </Grid>

        {isDisabled && (
          <Grid item style={{ width: '100%' }}>
            <Alert severity="error" style={{ marginTop: 10 }}>
              You need to subscribe to this market to use this simulator!
            </Alert>
          </Grid>
        )}

        <Grid item container spacing={4} style={{ width: '100%' }}>
          {inputFields.map((field, index) => (
            <>
              <InfoTooltip
                title={
                  field.tooltip && (
                    <TooltipCard
                      title={field.label}
                      content={field.tooltip}
                      // link="https://support.airbtics.com/"
                    />
                  )
                }
                arrow
              >
                <Grid item xs={6} key={index}>
                  <TextField
                    id={field.label}
                    label={field.label}
                    variant="outlined"
                    defaultValue={profitCalVals[field.key]}
                    value={profitCalVals[field.key]}
                    onChange={handleProfitCalValueChange(field.key)}
                    type="number"
                    error={!profitCalVals[field.key]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {exchangeRates[currency].symbol}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </InfoTooltip>
            </>
          ))}
        </Grid>

        <Grid item style={{ width: '100%' }}>
          <InfoTooltip
            title={
              <TooltipCard content="It is the expected amount of profit you will be generating each month. Monthly Revenue - Monthly Expense = Monthly Profit. It uses historical performance data of the listings you see on the map to calculate Monthly Revenue." />
            }
          >
            <Typography display="inline">
              Monthly Profit:{' '}
              <Box display="inline" fontWeight={500}>
                {exchangeRates[currency].symbol}
                {!isDisabled ? profitCalReturnObj['monthlyProfit'] : 'NAN'}
              </Box>{' '}
            </Typography>
          </InfoTooltip>
        </Grid>

        <Grid item style={{ width: '100%' }}>
          <Grid container className={classes.profitGraphRoot}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <ResponsiveContainer height={300} width="100%">
                <BarChart
                  data={profitCalReturnObj['graphVal']}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                  }}
                  onMouseEnter={hoverGraph}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="nMonths"
                    style={{
                      fontSize: '0.65rem',
                      fontFamily: 'Roboto',
                    }}
                  />
                  <YAxis
                    unit={exchangeRates[currency].symbol}
                    style={{
                      fontSize: '0.65rem',
                      fontFamily: 'Roboto',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: '0.85rem',
                      fontFamily: 'Roboto',
                    }}
                    cursor={{ fill: 'transparent' }}
                    content={<CustomTooltip classes={classes} active payload label />}
                  />
                  <Bar
                    type="monotone"
                    dataKey="nMonthsTotalExpense"
                    fill="#e69c5a"
                    stackId="a"
                    unit={exchangeRates[currency].symbol}
                    barSize={20}
                    background={{ fill: '	#FFFFFF' }}
                  />
                  <Bar
                    type="monotone"
                    dataKey="nMonthsTotalRev"
                    fill="#4d9cd6"
                    unit={exchangeRates[currency].symbol}
                    barSize={20}
                    stackId="b"
                    background={{ fill: '	#FFFFFF' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* </CardContent>
    </Card> */}
    </>
  );
};

export default RentalArbitrageSimulationCard;
